/* eslint-disable max-len */
import lightFormat from 'date-fns/lightFormat'
import { Draft } from '../../../state/reducers/draft/draft'
import { ContactInformation, JobContent } from '../../../state/reducers/jobs/jobs'
import { DashboardUser } from '../../../schema/user'
import objectHoldsAnyPrimitiveValue from '../objectHoldsAnyPrimitiveValue/objectHoldsAnyPrimitiveValue'
import { COMPANY_TYPE_DIRECT_EMPLOYER } from '../../../state/constants'

export type ApplyOption = 'ByEmail' | 'ByExternalUrl' | 'ByEasyApply'

type Args = {
  draft: Draft,
  user: DashboardUser,
  userCanTypeAlternativeContactInfo: boolean,
}

const includeUrlProtocol = (url: string) => {
  if (url.trim().toLowerCase().startsWith('http')) {
    return url
  }
  return `http://${url}`
}

// eslint-disable-next-line max-len
const draftToJobContent = ({ draft, draft: { contactInformation, applyVia, company, content, customFields, publicationPeriod },
  user, userCanTypeAlternativeContactInfo }: Args): JobContent => {

  // some fields are used purely for the presentational purposes and are not meant to be sent to the endpoint
  delete draft.company.availableNames
  delete draft.applyVia.applyOption

  const contactInfo: ContactInformation = userCanTypeAlternativeContactInfo ? {
    contact: {
      name: {
        firstName: contactInformation?.contact?.name?.firstName,
        lastName: contactInformation?.contact?.name?.lastName,
      },
      emailAddress: contactInformation?.contact?.emailAddress,
      phoneNumber: contactInformation?.contact?.phoneNumber,
    },
    address: {
      streetNameAndHouseNumber: contactInformation?.address?.streetNameAndHouseNumber,
      zipCode: contactInformation?.address?.zipCode,
      city: contactInformation?.address?.city,
    },
  } : {
    contact: {
      name: {
        firstName: user?.firstName,
        lastName: user?.lastName,
      },
      emailAddress: user?.emailAddress,
      phoneNumber: user?.phoneNumber,
    },
    address: user?.company?.postalAddress ? {
      streetNameAndHouseNumber: user?.company?.postalAddress?.streetNameAndHouseNumber,
      zipCode: user?.company?.postalAddress?.zipCode,
      city: user?.company?.postalAddress?.city,
    } : null,
  }

  const website = userCanTypeAlternativeContactInfo ? contactInformation?.website : user?.company?.website
  if (website !== '') { contactInfo.website = website }

  const enhancedContent: JobContent = {
    ...draft,
    applyVia: {
      ...applyVia,
    },
    company: {
      ...company,
      companyType: user.isVerified ? company.companyType : COMPANY_TYPE_DIRECT_EMPLOYER,
      huisstijl: company?.huisstijl || 'none',
    },
    contactInformation: objectHoldsAnyPrimitiveValue(contactInfo) ? contactInfo : null,
    content: {
      ...content,
    },
    customFields: customFields || [],
    publicationPeriod: {
      start: lightFormat(new Date(publicationPeriod.start), 'yyyy-MM-dd'),
      end: lightFormat(new Date(publicationPeriod.end), 'yyyy-MM-dd'),
    },
  }

  const companyWebsite = company?.website
  if (typeof companyWebsite === 'string' && companyWebsite !== '') {
    enhancedContent.company.website = includeUrlProtocol(companyWebsite)
  }

  // only include non-empty top-level properties
  const sanitized = Object.keys(enhancedContent).filter(key => {
    if (key === 'customFields') { return true }
    return Object.keys(enhancedContent[key] || {}).length > 0
  }).reduce((obj, key) => {
    // eslint-disable-next-line no-param-reassign
    obj[key] = enhancedContent[key]
    return obj
  }, {})

  return sanitized as JobContent
}

export default draftToJobContent
